/**
 * Data Processing Hooks for WebGLChart
 * Handles data transformation for display
 */

import { useMemo } from 'react';
import { DataPoint, ProcessedChartData, DisplayData } from '../types';

/**
 * Process raw data into timestamps and values arrays
 */
export function useProcessedData(
  data: DataPoint[],
  seriesName: string
): ProcessedChartData {
  return useMemo(() => {
    if (!data || data.length === 0 || !seriesName) {
      return { timestamps: [], values: [], validCount: 0, yMin: 0, yMax: 0 };
    }

    const timestamps: number[] = [];
    const values: (number | null)[] = [];
    let yMin = Infinity;
    let yMax = -Infinity;
    let validCount = 0;

    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      const raw = d[seriesName];

      // Parse UTC - backend sends milliseconds as number
      let ts: number;
      if (typeof d.UTC === "number") {
        ts = d.UTC / 1000; // ms → seconds for chart
      } else {
        ts = new Date(d.UTC as string).getTime() / 1000;
      }

      if (!Number.isFinite(ts)) continue;

      timestamps.push(ts);

      // Handle value - can be number, string, null, or "None"
      let v: number | null = null;
      if (raw === null || raw === undefined || raw === "None" || raw === "NaN") {
        v = null;
      } else if (typeof raw === "number") {
        v = Number.isFinite(raw) ? raw : null;
      } else if (typeof raw === "string") {
        const parsed = Number(raw);
        v = Number.isFinite(parsed) ? parsed : null;
      }

      values.push(v);

      if (v !== null) {
        validCount++;
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }

    // Handle edge case where all values are null
    if (yMin === Infinity) yMin = 0;
    if (yMax === -Infinity) yMax = 1;

    return { timestamps, values, validCount, yMin, yMax };
  }, [data, seriesName]);
}

/**
 * Prepare display data - returns all points without downsampling
 */
export function useDisplayData(
  processedData: ProcessedChartData
): DisplayData {
  return useMemo(() => {
    const { timestamps, values } = processedData;

    if (timestamps.length === 0) {
      return { timestamps: [], values: [] };
    }

    return {
      timestamps: [...timestamps],
      values: [...values]
    };
  }, [processedData]);
}
